import { getTodayDateKey } from './dateutils'
import {
  getPlayerGrade,
  getPlayerLastInitial,
  getPlayerName,
  getPlayerPrefix,
} from './localStorage'
import { hasSupabaseConfig, supabase } from './supabase'
import { getSolutionForGrade } from './words'

// Returns the top 10 MVPs by new Weighted Difficulty Score
export const computeTopMvpList = (entries: LeaderboardEntry[]): MvpEntry[] => {
  const valid = entries.filter((e) => e.name && String(e.name).trim())
  if (valid.length === 0) return []

  const map = new Map<string, LeaderboardEntry[]>()
  for (const e of valid) {
    const key = e.name.toLowerCase().trim()
    map.set(key, [...(map.get(key) || []), e])
  }

  const stats: MvpEntry[] = []
  map.forEach((games) => {
    if (games.length < 3) return
    let totalWeightedPoints = 0
    let totalWins = 0
    let totalGuesses = 0
    let totalGames = games.length
    games.forEach((g) => {
      let points = 0
      if (g.won) {
        if (g.gameType === 'bonus') {
          points = 1.5
        } else if (g.gameType === 'teachers') {
          points = 1.2
        } else if (
          g.gameType === `grade${g.grade}` ||
          (g.gameType === 'daily' && g.grade > 0)
        ) {
          points = 1.0
        } else if (/^grade\d+$/.test(g.gameType)) {
          points = 0.8
        }
        totalWins++
      }
      totalWeightedPoints += points
      totalGuesses += g.guessCount
    })
    const winRate = totalGames > 0 ? totalWins / totalGames : 0
    const avgGuesses = totalGames > 0 ? totalGuesses / totalGames : 0
    const score =
      avgGuesses > 0 ? (totalWeightedPoints / avgGuesses) * winRate : 0
    stats.push({
      name: games[0].name,
      grade: games[0].grade,
      totalGames,
      activeDays: getActiveDayCount(games),
      wins: totalWins,
      winRate,
      avgGuesses,
      score,
    })
  })
  stats.sort((a, b) => b.score - a.score)
  return stats.slice(0, 10)
}

const SUPABASE_TABLES = {
  gameSubmissions: 'game_submissions',
  keystrokeLogs: 'keystroke_logs',
  playerProfiles: 'player_profiles',
  playerStateSnapshots: 'player_state_snapshots',
  signupEvents: 'signup_events',
} as const

export type GuessData = {
  word: string
  timeSec: number
  keystrokes: number
  deletes: number
}

// ─── Live Keystroke Tracking ────────────────────────────────────────────────

export type KeystrokeEvent = {
  timestamp: string
  keyType:
    | 'char' // letter successfully added to current guess
    | 'char_blocked' // letter pressed but couldn't be added
    | 'delete' // backspace, removed a character
    | 'delete_empty' // backspace pressed when input was already empty
    | 'delete_blocked' // backspace blocked by modal/animation
    | 'enter_submit' // valid guess submitted
    | 'enter_blocked' // enter pressed but rejected
  keyValue: string // the actual letter, 'BACKSPACE', or 'ENTER'
  reason?: string // why blocked: 'word_full'|'game_over'|'clearing'|'modal_open'|'too_short'|'invalid_word'|'hard_mode'
  guessNum: number // which guess row (0-based)
  inputBefore: string // current guess before the keypress
  inputAfter: string // current guess after the keypress
}

export type KeystrokeBatchPayload = {
  action: 'keystrokes'
  sessionId: string
  playerName: string
  grade: string
  date: string
  gameType: string
  events: KeystrokeEvent[]
}

export const submitKeystrokeBatch = async (
  sessionId: string,
  meta: { playerName: string; grade: string; date: string; gameType: string },
  events: KeystrokeEvent[]
): Promise<void> => {
  if (!events.length) return
  if (!hasSupabaseConfig || !supabase) return

  try {
    const safePlayerName = sanitizePlayerName(meta.playerName)
    const normalizedGrade = normalizeLegacyGrade(meta.grade)
    const playerKey = buildPlayerStateKey(safePlayerName, normalizedGrade)
    const rows = events.map((event, index) => ({
      session_id: sessionId,
      player_key: playerKey,
      player_name: safePlayerName,
      player_name_key: normalizeNameKey(meta.playerName),
      grade: Number(normalizedGrade) || 0,
      game_date: meta.date,
      game_type: meta.gameType,
      event_timestamp: event.timestamp,
      sequence_number: index,
      key_type: event.keyType,
      key_value: event.keyValue,
      reason: event.reason ?? null,
      guess_number: event.guessNum,
      input_before: event.inputBefore,
      input_after: event.inputAfter,
      received_at: new Date().toISOString(),
    }))

    const { error } = await supabase
      .from(SUPABASE_TABLES.keystrokeLogs)
      .insert(rows)
    if (!error) return
    console.error('Failed to insert keystroke batch into Supabase:', error)
  } catch (error) {
    console.error('Failed to write keystroke batch to Supabase:', error)
  }
}

export const sendKeystrokeBatch = submitKeystrokeBatch

export type SignupEvent = {
  action: 'signup'
  playerName: string
  grade: string
  registeredAtClient: string
  source: string
  userAgent: string
  screenWidth: number
  screenHeight: number
}

export type CloudPlayerState = {
  updatedAt: string
  state: Record<string, string>
}

export const submitSignupEvent = async (
  playerName: string,
  grade: string,
  source = 'grade_modal'
): Promise<void> => {
  if (!playerName || !grade) return
  if (!hasSupabaseConfig || !supabase) return

  const safePlayerName = sanitizePlayerName(playerName)
  const normalizedGrade = normalizeLegacyGrade(grade)
  const playerKey = buildPlayerStateKey(safePlayerName, normalizedGrade)
  const registeredAtClient = new Date().toISOString()

  try {
    const profile = {
      player_key: playerKey,
      player_name: safePlayerName,
      player_name_key: normalizeNameKey(safePlayerName),
      grade: Number(normalizedGrade) || 0,
      source,
      registered_at_client: registeredAtClient,
      updated_at: registeredAtClient,
    }

    const { error: profileError } = await supabase
      .from(SUPABASE_TABLES.playerProfiles)
      .upsert(profile, { onConflict: 'player_key' })

    if (profileError) {
      console.error(
        'Failed to upsert player profile in Supabase:',
        profileError
      )
    }

    const { data: existingSnapshot, error: snapshotReadError } = await supabase
      .from(SUPABASE_TABLES.playerStateSnapshots)
      .select('player_key')
      .eq('player_key', playerKey)
      .limit(1)

    if (snapshotReadError) {
      console.error(
        'Failed to check player snapshot before signup bootstrap:',
        snapshotReadError
      )
    } else if (!existingSnapshot || existingSnapshot.length === 0) {
      const { error: snapshotWriteError } = await supabase
        .from(SUPABASE_TABLES.playerStateSnapshots)
        .insert({
          player_key: playerKey,
          player_name: safePlayerName,
          player_name_key: normalizeNameKey(safePlayerName),
          grade: Number(normalizedGrade) || 0,
          state: {},
          device: navigator.userAgent || '',
          app_version: 'v2',
          updated_at: registeredAtClient,
        })

      if (snapshotWriteError) {
        console.error(
          'Failed to bootstrap player snapshot in Supabase:',
          snapshotWriteError
        )
      }
    }

    const { error: signupError } = await supabase
      .from(SUPABASE_TABLES.signupEvents)
      .insert({
        player_key: playerKey,
        player_name: safePlayerName,
        player_name_key: normalizeNameKey(safePlayerName),
        grade: Number(normalizedGrade) || 0,
        registered_at_client: registeredAtClient,
        source,
        user_agent: navigator.userAgent || '',
        screen_width: window.innerWidth || 0,
        screen_height: window.innerHeight || 0,
      })

    if (!signupError) return
    console.error('Failed to insert signup event into Supabase:', signupError)
  } catch (error) {
    console.error('Failed to write signup data to Supabase:', error)
  }
}

export const submitNameStepRegistration = async (
  playerName: string,
  grade: string
): Promise<void> => {
  if (!playerName || !grade) return
  if (!hasSupabaseConfig || !supabase) return

  const safeName = sanitizePlayerName(playerName)
  const normalizedGrade = normalizeLegacyGrade(grade)

  try {
    await supabase.from(SUPABASE_TABLES.signupEvents).insert({
      player_key: `${normalizeNameKey(safeName)}|${normalizedGrade}|partial`,
      player_name: safeName,
      player_name_key: normalizeNameKey(safeName),
      grade: Number(normalizedGrade) || 0,
      registered_at_client: new Date().toISOString(),
      source: 'name_step',
      user_agent: navigator.userAgent || '',
      screen_width: window.innerWidth || 0,
      screen_height: window.innerHeight || 0,
    })
  } catch {
    // Never block registration on analytics write failure.
  }
}

export type GameSubmission = {
  name: string
  grade: string
  date: string
  word: string
  won: boolean
  guessCount: number
  gameType: 'daily' | 'bonus' | 'teachers' | 'grade' | `grade${string}`
  gameStartTime: string
  gameEndTime: string
  totalDurationSec: number
  timeToFirstGuessSec: number
  device: string
  screenWidth: number
  guesses: GuessData[]
}

export type LeaderboardEntry = {
  name: string
  grade: number
  date: string
  word: string
  won: boolean
  guessCount: number
  gameType: string
  totalDurationSec: number
  gameStartTime: string
  playerKey?: string
  // Server insert time — used to tell a graduated senior's old submissions
  // (made before that grade's rollover) apart from a current senior's.
  submittedAt: string
}

export type MvpEntry = {
  name: string
  grade: number
  totalGames: number
  activeDays: number
  wins: number
  winRate: number
  avgGuesses: number
  score: number
}

const getDateFromYmd = (dateLike: string): Date | null => {
  const ymd = String(dateLike || '').slice(0, 10)
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2]) - 1
  const d = Number(m[3])
  return new Date(y, mo, d)
}

const getExpectedDailyWord = (entry: LeaderboardEntry): string | null => {
  const day = getDateFromYmd(entry.date)
  if (!day) return null
  // Use the grade-specific word list for this entry's grade so the check
  // works correctly regardless of which grade the current viewer is logged in as.
  return getSolutionForGrade(day, Number(entry.grade) || 0)
}

const isHistoricalPlaceholderEntry = (entry: LeaderboardEntry): boolean =>
  String(entry.date || '').startsWith('1970')

const getAllTimeDayBucket = (
  entry: LeaderboardEntry,
  index: number
): string => {
  if (isHistoricalPlaceholderEntry(entry)) {
    return `historical-${index}`
  }
  return String(entry.date || '').slice(0, 10)
}

const getActiveDayCount = (entries: LeaderboardEntry[]): number => {
  let historicalCount = 0
  const datedDays = new Set<string>()

  for (const entry of entries) {
    if (isHistoricalPlaceholderEntry(entry)) {
      historicalCount += 1
      continue
    }

    const day = String(entry.date || '').slice(0, 10)
    if (day) {
      datedDays.add(day)
    }
  }

  return historicalCount + datedDays.size
}

export const isTrueDailyEntry = (entry: LeaderboardEntry): boolean => {
  const type = String(entry.gameType || '')
    .toLowerCase()
    .trim()
  const rowWord = String(entry.word || '')
    .toUpperCase()
    .trim()
  const expectedWord = getExpectedDailyWord(entry)

  if (type === 'daily') {
    return !expectedWord || !rowWord || rowWord === expectedWord
  }
  if (String(entry.grade) === '0' && type === 'teachers') {
    return !expectedWord || !rowWord || rowWord === expectedWord
  }

  // Own-grade daily: gameType is grade{playerGrade} (e.g. "grade9" for a freshman).
  // We trust the game_type column; strict word-matching is intentionally removed
  // because grade word lists change as students enroll/graduate, causing idx%length
  // to map to different names for historical dates, silently dropping all entries
  // for any grade whose list length changed since they played.
  if (type === `grade${String(entry.grade)}`) {
    return true
  }

  // Older Google Sheets rows stored own-grade daily plays as plain "grade".
  if (type === 'grade') {
    return true
  }

  return false
}

export const computeMvp = (entries: LeaderboardEntry[]): MvpEntry | null => {
  const valid = entries.filter((e) => e.name && String(e.name).trim())
  if (valid.length === 0) return null

  const map = new Map<string, LeaderboardEntry[]>()
  for (const e of valid) {
    const key = e.name.toLowerCase().trim()
    map.set(key, [...(map.get(key) || []), e])
  }

  let maxGames = 1
  let maxWins = 1
  let maxActiveDays = 1
  map.forEach((games) => {
    if (games.length > maxGames) maxGames = games.length
    const wins = games.filter((g) => g.won).length
    if (wins > maxWins) maxWins = wins
    const activeDays = getActiveDayCount(games)
    if (activeDays > maxActiveDays) maxActiveDays = activeDays
  })

  const stats: MvpEntry[] = []
  map.forEach((games) => {
    if (games.length < 3) return

    const wins = games.filter((g) => g.won)
    const winRate = wins.length / games.length
    const avgGuesses =
      wins.length > 0
        ? wins.reduce((s, g) => s + g.guessCount, 0) / wins.length
        : 7
    const activeDays = getActiveDayCount(games)

    const volumeScore = (games.length / maxGames) * 40
    const winVolumeScore = (wins.length / maxWins) * 25
    const winRateScore = winRate * 20
    const efficiencyScore = (Math.max(0, 6 - avgGuesses) / 6) * 10
    const activeDaysScore = (activeDays / maxActiveDays) * 5
    const score =
      volumeScore +
      winVolumeScore +
      winRateScore +
      efficiencyScore +
      activeDaysScore

    stats.push({
      name: games[0].name,
      grade: games[0].grade,
      totalGames: games.length,
      activeDays,
      wins: wins.length,
      winRate,
      avgGuesses: wins.length > 0 ? avgGuesses : 0,
      score,
    })
  })

  if (stats.length === 0) return null
  return stats.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score
    if (b.totalGames !== a.totalGames) return b.totalGames - a.totalGames
    if (b.wins !== a.wins) return b.wins - a.wins
    if (b.activeDays !== a.activeDays) return b.activeDays - a.activeDays
    return a.avgGuesses - b.avgGuesses
  })[0]
}

// Per-player current win streak (consecutive daily wins ending today or yesterday)
export const computeStreaks = (
  entries: LeaderboardEntry[]
): Map<string, number> => {
  const dailyWins = entries.filter(
    (e) =>
      isTrueDailyEntry(e) &&
      e.won &&
      e.name &&
      !String(e.date).startsWith('1970')
  )

  const byPlayer = new Map<string, Set<string>>()
  for (const e of dailyWins) {
    const key = e.name.toLowerCase().trim()
    const dateStr = String(e.date).slice(0, 10)
    if (!byPlayer.has(key)) byPlayer.set(key, new Set())
    byPlayer.get(key)!.add(dateStr)
  }

  const streaks = new Map<string, number>()
  const todayStr = new Date().toISOString().split('T')[0]
  const yesterdayStr = new Date(Date.now() - 86400000)
    .toISOString()
    .split('T')[0]

  byPlayer.forEach((winDates, key) => {
    let startStr: string
    if (winDates.has(todayStr)) {
      startStr = todayStr
    } else if (winDates.has(yesterdayStr)) {
      startStr = yesterdayStr
    } else {
      streaks.set(key, 0)
      return
    }

    let streak = 0
    const check = new Date(startStr + 'T12:00:00Z')
    while (streak < 365) {
      const ds = check.toISOString().split('T')[0]
      if (winDates.has(ds)) {
        streak++
        check.setUTCDate(check.getUTCDate() - 1)
      } else {
        break
      }
    }
    streaks.set(key, streak)
  })

  return streaks
}

// Submit historical games reconstructed from localStorage stats when a player
// creates a new account for the first time. winDistribution[i] = wins in i+1 guesses.
// gamesFailed = total losses. Submitted with placeholder date/word since we only
// have aggregate data. These are backfilled into Supabase using placeholder
// dates so they stay out of date-based leaderboard views while preserving
// all-time volume and win-rate history.
export const submitHistoricalStats = async (
  name: string,
  grade: string,
  winDistribution: number[],
  gamesFailed: number
) => {
  if (!hasSupabaseConfig || !supabase) return

  const placeholder = '1970-01-01'
  const now = new Date().toISOString()
  const base = {
    gameType: 'daily' as const,
    date: placeholder,
    word: 'XXXXX',
    gameStartTime: now,
    gameEndTime: now,
    totalDurationSec: 0,
    timeToFirstGuessSec: 0,
    device: 'historical',
    screenWidth: 0,
    guesses: [],
  }

  const submissions: GameSubmission[] = []

  winDistribution.forEach((count, index) => {
    for (let submitCount = 0; submitCount < count; submitCount++) {
      submissions.push({
        ...base,
        name,
        grade,
        won: true,
        guessCount: index + 1,
      })
    }
  })

  for (let submitCount = 0; submitCount < gamesFailed; submitCount++) {
    submissions.push({ ...base, name, grade, won: false, guessCount: 6 })
  }

  for (const submission of submissions) {
    try {
      await submitGameData(submission)
    } catch {
      // Never block account creation on historical backfill failures.
    }
  }
}

export const submitGameData = async (data: GameSubmission): Promise<void> => {
  if (!hasSupabaseConfig || !supabase) return

  const safeName = sanitizePlayerName(data.name)
  const normalizedGrade = normalizeLegacyGrade(data.grade)
  const playerKey = buildPlayerStateKey(safeName, normalizedGrade)

  try {
    const { error } = await supabase
      .from(SUPABASE_TABLES.gameSubmissions)
      .insert({
        player_key: playerKey,
        player_name: safeName,
        player_name_key: normalizeNameKey(safeName),
        grade: Number(normalizedGrade) || 0,
        game_date: data.date,
        word: data.word,
        won: data.won,
        guess_count: data.guessCount,
        game_type: data.gameType,
        game_start_time: data.gameStartTime,
        game_end_time: data.gameEndTime,
        total_duration_sec: data.totalDurationSec,
        time_to_first_guess_sec: data.timeToFirstGuessSec,
        device: data.device,
        screen_width: data.screenWidth,
        guesses: data.guesses,
      })

    if (!error) return
    console.error('Failed to insert game submission into Supabase:', error)
  } catch (error) {
    console.error('Failed to write game submission to Supabase:', error)
  }
}

const normalizeLegacyGrade = (rawGrade: string): string => {
  const clean = String(rawGrade || '')
    .replace(/"/g, '')
    .trim()
  // Only map legacy codes, never touch valid grades
  const legacyMap: Record<string, string> = {
    '8': '11',
    '27': '11',
    '7': '10',
    '28': '10',
  }
  if (['9', '10', '11', '12', '0'].includes(clean)) return clean
  return legacyMap[clean] || clean
}

const normalizeNameKey = (name: string): string =>
  String(name || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()

const buildPlayerStateKey = (playerName: string, grade: string): string =>
  `${normalizeNameKey(playerName)}|${normalizeLegacyGrade(grade)}`

// Strips characters that have no place in a school player name.
// Allows letters (including accented), spaces, apostrophes, and hyphens.
const SAFE_NAME_RE = /^[\p{L}\p{M}'\- ]{1,60}$/u
const sanitizePlayerName = (name: string): string => {
  const trimmed = String(name || '').trim()
  if (!SAFE_NAME_RE.test(trimmed)) {
    // Remove any character that is not a letter, accent, space, apostrophe, or hyphen.
    return trimmed
      .replace(/[^\p{L}\p{M}'\- ]/gu, '')
      .slice(0, 60)
      .trim()
  }
  return trimmed
}
export const syncPlayerStateToCloud = async (
  playerName: string,
  grade: string,
  state: Record<string, string>
): Promise<void> => {
  const cleanName = String(playerName || '').trim()
  const cleanGrade = normalizeLegacyGrade(grade)
  if (!cleanName || !cleanGrade) return

  if (!hasSupabaseConfig || !supabase) return

  try {
    const now = new Date().toISOString()
    const { error } = await supabase
      .from(SUPABASE_TABLES.playerStateSnapshots)
      .upsert(
        {
          player_key: buildPlayerStateKey(cleanName, cleanGrade),
          player_name: cleanName,
          player_name_key: normalizeNameKey(cleanName),
          grade: Number(cleanGrade) || 0,
          state,
          device: navigator.userAgent || '',
          app_version: 'v2',
          updated_at: now,
        },
        { onConflict: 'player_key' }
      )
    if (!error) return
    console.error('Failed to sync player state to Supabase:', error)
  } catch (error) {
    console.error('Failed to write cloud state to Supabase:', error)
  }
}

export const fetchPlayerStateFromCloud = async (
  playerName: string,
  grade: string
): Promise<CloudPlayerState | null> => {
  const key = buildPlayerStateKey(playerName, grade)
  if (!key || key === '|') return null

  if (!hasSupabaseConfig || !supabase) return null

  try {
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.playerStateSnapshots)
      .select('updated_at, state')
      .eq('player_key', key)
      .order('updated_at', { ascending: false })
      .limit(1)

    if (error) {
      console.error('Failed to fetch cloud state from Supabase:', error)
    } else if (data && data.length > 0) {
      const latest = data[0] as {
        updated_at: string
        state: Record<string, string>
      }
      return { updatedAt: latest.updated_at, state: latest.state || {} }
    }
  } catch (error) {
    console.error('Failed to read cloud state from Supabase:', error)
  }
  return null
}

export type AllTimeEntry = {
  name: string
  grade: number
  // True when this person no longer has a roster row (grade rollover
  // removes graduated seniors) — their stored `grade` is just whatever it
  // last was when they were still enrolled.
  isAlum: boolean
  totalDays: number
  wins: number
  winRate: number
  avgGuesses: number
}

const isBetterResult = (a: LeaderboardEntry, b: LeaderboardEntry): boolean => {
  if (a.won !== b.won) return a.won && !b.won
  if (a.guessCount !== b.guessCount) return a.guessCount < b.guessCount
  return a.totalDurationSec < b.totalDurationSec
}

// Maps every currently-rostered player's name_key to their current grade.
// game_submissions.grade is frozen at whatever it was the day that game was
// played — a rising senior's sophomore-year games still say grade 10 — so
// the all-time view needs this to show where everyone stands *now* instead
// of a mix of stale historical grades.
//
// Goes through the get_current_roster() RPC rather than a direct SELECT:
// player_profiles has RLS enabled with only INSERT/UPDATE policies (no
// SELECT policy), specifically so a broad SELECT can never expose the
// microsoft_email column — a plain .from(...).select(...) here would
// silently return zero rows, not an error, since RLS denies by default
// when no policy matches. See supabase/migrations/007-current-roster-rpc.sql.
export const fetchCurrentRoster = async (): Promise<Record<string, number>> => {
  if (!hasSupabaseConfig || !supabase) return {}
  try {
    const { data, error } = await supabase.rpc('get_current_roster')
    if (error || !data) return {}
    const roster: Record<string, number> = {}
    for (const row of data) {
      const key = row.player_name_key ? String(row.player_name_key) : ''
      if (!key) continue
      roster[key] = Number(row.grade) || 0
    }
    return roster
  } catch {
    return {}
  }
}

export const computeAllTimeLeaderboard = (
  entries: LeaderboardEntry[],
  currentGradeByNameKey: Record<string, number> = {}
): AllTimeEntry[] => {
  const map = new Map<string, LeaderboardEntry[]>()
  for (const e of entries) {
    // Prefer the stored player_key (name|grade) so two different players
    // who happen to share a display name get separate rows. Fall back to
    // name|grade derived locally for legacy rows that pre-date player_key.
    const key = e.playerKey || `${e.name.toLowerCase().trim()}|${e.grade}`
    map.set(key, [...(map.get(key) || []), e])
  }
  const result: AllTimeEntry[] = []
  map.forEach((games) => {
    const byDate = new Map<string, LeaderboardEntry>()
    games.forEach((g, index) => {
      const dateKey = getAllTimeDayBucket(g, index)
      if (!dateKey) return
      const existing = byDate.get(dateKey)
      if (!existing || isBetterResult(g, existing)) {
        byDate.set(dateKey, g)
      }
    })

    const dayResults = Array.from(byDate.values())
    const wins = dayResults.filter((g) => g.won)
    const avgGuesses =
      wins.length > 0
        ? wins.reduce((s, g) => s + g.guessCount, 0) / wins.length
        : 0
    const historicalGrade = games[0].grade
    const nameKey = normalizeNameKey(games[0].name)
    const currentGrade = currentGradeByNameKey[nameKey]
    const isOnRoster = currentGrade !== undefined
    result.push({
      name: games[0].name,
      grade: isOnRoster ? currentGrade : historicalGrade,
      isAlum: !isOnRoster && historicalGrade === 12,
      totalDays: dayResults.length,
      wins: wins.length,
      winRate: dayResults.length > 0 ? wins.length / dayResults.length : 0,
      avgGuesses,
    })
  })
  return result.sort((a, b) => {
    if (b.totalDays !== a.totalDays) return b.totalDays - a.totalDays
    if (b.wins !== a.wins) return b.wins - a.wins
    return b.winRate - a.winRate
  })
}

export const fetchLeaderboard = async (
  date?: string,
  grade?: string,
  allTime?: boolean
): Promise<LeaderboardEntry[]> => {
  if (!hasSupabaseConfig || !supabase) return []

  const today = allTime ? '' : date || getTodayDateKey()
  const selectedGrade = grade ? normalizeLegacyGrade(grade) : ''

  const processRows = (rows: any[]): LeaderboardEntry[] => {
    const results: LeaderboardEntry[] = []
    for (const row of rows) {
      const rawName = row.player_name ? String(row.player_name) : ''
      const rowType = String(row.game_type || 'daily')
        .toLowerCase()
        .trim()
      const rowGrade =
        row.grade != null ? normalizeLegacyGrade(String(row.grade)) : ''
      const finalName = rawName
      const finalGrade = rowGrade

      if (rowType === 'signup') continue
      if (selectedGrade && finalGrade !== selectedGrade) continue
      if (!finalName || !String(finalName).trim()) continue

      results.push({
        name: finalName,
        grade: Number(finalGrade) || 0,
        date: row.game_date ? String(row.game_date) : '',
        word: row.word ? String(row.word) : '',
        won: Boolean(row.won),
        guessCount: Number(row.guess_count) || 0,
        gameType: rowType,
        totalDurationSec: Number(row.total_duration_sec) || 0,
        gameStartTime: row.game_start_time ? String(row.game_start_time) : '',
        playerKey: row.player_key ? String(row.player_key) : '',
        submittedAt: row.created_at ? String(row.created_at) : '',
      })
    }
    return results
  }

  try {
    // For all-time queries, paginate in batches of 1000 to bypass Supabase's
    // server-side max_rows cap (default 1000). Without pagination, players added
    // after the first 1000 historical migration rows are silently cut off.
    if (allTime) {
      const PAGE_SIZE = 1000
      const allResults: LeaderboardEntry[] = []
      let from = 0

      while (true) {
        let pageQuery = supabase
          .from(SUPABASE_TABLES.gameSubmissions)
          .select(
            'player_key, player_name, grade, game_date, word, won, guess_count, game_type, total_duration_sec, game_start_time, created_at'
          )
          .order('id', { ascending: true })
          .range(from, from + PAGE_SIZE - 1)

        if (selectedGrade) {
          pageQuery = pageQuery.eq('grade', Number(selectedGrade) || 0)
        }

        const { data: pageData, error: pageError } = await pageQuery

        if (pageError) {
          console.error('Failed to fetch leaderboard from Supabase:', pageError)
          break
        }

        allResults.push(...processRows(pageData ?? []))

        if (!pageData || pageData.length < PAGE_SIZE) break
        from += PAGE_SIZE
      }

      allResults.sort((a, b) => {
        if (a.won !== b.won) return a.won ? -1 : 1
        if (a.guessCount !== b.guessCount) return a.guessCount - b.guessCount
        return a.totalDurationSec - b.totalDurationSec
      })

      return allResults
    }

    // Today-only query — always well under the row limit.
    let query = supabase
      .from(SUPABASE_TABLES.gameSubmissions)
      .select(
        'player_name, grade, game_date, word, won, guess_count, game_type, total_duration_sec, game_start_time, created_at'
      )

    if (today) {
      query = query.eq('game_date', today)
    }

    if (selectedGrade) {
      query = query.eq('grade', Number(selectedGrade) || 0)
    }

    const { data, error } = await query

    if (error) {
      console.error('Failed to fetch leaderboard from Supabase:', error)
    } else {
      const results = processRows(data ?? [])

      results.sort((a, b) => {
        if (a.won !== b.won) return a.won ? -1 : 1
        if (a.guessCount !== b.guessCount) return a.guessCount - b.guessCount
        return a.totalDurationSec - b.totalDurationSec
      })

      return results
    }
  } catch (err) {
    console.error('Failed to fetch leaderboard from Supabase:', err)
  }

  return []
}

// ─── Today's Leader ─────────────────────────────────────────────────────────

export type TodayLeader = {
  name: string
  guessCount: number
}

export const fetchTodayLeader = async (
  grade: string,
  date: string
): Promise<TodayLeader | null> => {
  if (!hasSupabaseConfig || !supabase) return null

  const normalizedGrade = normalizeLegacyGrade(grade)

  const { data, error } = await supabase
    .from(SUPABASE_TABLES.gameSubmissions)
    .select('player_name, guess_count, total_duration_sec, game_type')
    .eq('game_date', date)
    .eq('grade', Number(normalizedGrade) || 0)
    .eq('won', true)
    .neq('game_type', 'bonus')
    .order('guess_count', { ascending: true })
    .order('total_duration_sec', { ascending: true })
    .limit(10)

  if (error) {
    console.error('[fetchTodayLeader] Supabase error:', error)
    return null
  }
  if (!data || data.length === 0) return null

  // Prefer a daily-type win; fall back to any won row if there's no daily yet
  const dailyGameType =
    normalizedGrade === '0' ? 'teachers' : `grade${normalizedGrade}`
  const best =
    data.find((r: any) => String(r.game_type || '') === dailyGameType) ??
    data[0]

  const displayName = String(best.player_name || '')

  return { name: displayName, guessCount: Number(best.guess_count) }
}

// ─── Microsoft Auth — Email ↔ Account Linking ────────────────────────────────

export type LinkedAccount = {
  playerName: string
  grade: string
  lastInitial?: string
  prefix?: string
}

const TEACHER_PREFIXES = ['Mr.', 'Mrs.', 'Ms.', 'Miss', 'Dr.', 'Coach', 'Prof.']

/**
 * Looks up a player profile by Microsoft email using a secure database
 * function (never exposes the email column via a broad SELECT).
 * Returns the parsed account info, or null if not found.
 */
export const lookupAccountByEmail = async (
  email: string
): Promise<LinkedAccount | null> => {
  if (!hasSupabaseConfig || !supabase) return null
  try {
    const { data, error } = await supabase.rpc('lookup_player_by_email', {
      p_email: email.toLowerCase().trim(),
    })
    if (error || !data) return null
    const row = Array.isArray(data) ? data[0] : data
    if (!row) return null

    const name = String(row.player_name || '')
    const gradeStr = String(row.grade)
    const parts = name.split(' ')

    if (TEACHER_PREFIXES.includes(parts[0])) {
      return {
        playerName: parts.slice(1).join(' '),
        grade: gradeStr,
        prefix: parts[0],
      }
    }
    const lastInitial = parts.length > 1 ? parts[parts.length - 1] : ''
    const firstName = lastInitial ? parts.slice(0, -1).join(' ') : name
    return {
      playerName: firstName,
      grade: gradeStr,
      lastInitial: lastInitial || undefined,
    }
  } catch {
    return null
  }
}

/**
 * Writes the Microsoft email into the player's profile row so they can be
 * found by email on future logins from any device.
 * Reads the current player identity from localStorage.
 * Safe to call fire-and-forget.
 */
export const linkEmailToCurrentAccount = async (
  email: string
): Promise<void> => {
  if (!hasSupabaseConfig || !supabase) return

  const playerName = getPlayerName()
  const prefix = getPlayerPrefix()
  const lastInitial = getPlayerLastInitial()
  const rawGrade = getPlayerGrade()

  if (!playerName || !rawGrade) return

  const displayName = prefix
    ? `${prefix} ${playerName}`
    : lastInitial
    ? `${playerName} ${lastInitial}`
    : playerName

  const normalizedGrade = normalizeLegacyGrade(rawGrade)
  const playerKey = buildPlayerStateKey(displayName, normalizedGrade)
  const safeEmail = email.toLowerCase().trim()

  try {
    // Upsert so this works even if the profile row hasn't been created yet.
    await supabase.from(SUPABASE_TABLES.playerProfiles).upsert(
      {
        player_key: playerKey,
        player_name: sanitizePlayerName(displayName),
        player_name_key: normalizeNameKey(displayName),
        grade: Number(normalizedGrade) || 0,
        microsoft_email: safeEmail,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'player_key' }
    )
  } catch {
    // Best-effort — never block gameplay on linking failures.
  }
}
